import { useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";

import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import SpaceSelect from "components/SpaceSelect";
import { spaceAccessLevelOptions } from "constants/space";
import Box from "ds/components/Box";
import FormArrayField from "ds/components/Form/ArrayField";
import FormField from "ds/components/Form/Field";
import FormFieldRowWrapper from "ds/components/Form/Field/RowWrapper";
import Select from "ds/components/Select";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import { getDocsUrl } from "utils/getDocsUrl";

import FormFieldsSpacesRoleOption from "./RoleOption";
import styles from "./styles.module.css";
import { FormFieldSpacesProps, TFieldValues } from "./types";
import { useSpacesOptions } from "./useSpacesOptions";
import { validateAccessLevel, validateSpace } from "./validators";

const EMPTY_VALUE = { space: undefined, spaceAccessLevel: undefined };

const FormFieldSpaces = ({ allowEmpty, isDisabled, analyticsPage }: FormFieldSpacesProps) => {
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: analyticsPage,
  });

  const { watch } = useFormContext<TFieldValues>();
  const spaceFields = watch("spaces");

  const onRemove = () => {
    trackSegmentAnalyticsEvent?.("Space Access Delete");
  };

  const onAdd = () => {
    trackSegmentAnalyticsEvent?.("Add Another Access Click");
  };

  const validateSpaceField = useCallback(
    (value: string | undefined, index: number) => {
      return validateSpace({ value, index, spaceFields, allowEmpty });
    },
    [allowEmpty, spaceFields]
  );

  const validateAccessLevelField = useCallback(
    (value: string | undefined, index: number) => {
      return validateAccessLevel({ value, index, spaceFields, allowEmpty });
    },
    [allowEmpty, spaceFields]
  );

  const { disabledSpaces, areSpacesOptionsAvailable, canAddMoreRows } = useSpacesOptions();

  return (
    <Box margin="large 0 0">
      <FormArrayField<TFieldValues, "spaces">
        name="spaces"
        addButtonLabel="Add another access"
        emptyValue={EMPTY_VALUE}
        onRemove={onRemove}
        onAdd={onAdd}
        disabled={isDisabled}
        isAddDisabled={!canAddMoreRows}
      >
        {({ value, index, control, suffix }) => (
          <Box align="start" gap="medium" fullWidth>
            <Controller
              name={`spaces.${index}.space`}
              control={control}
              rules={{
                validate: (val) => validateSpaceField(val, index),
              }}
              render={({ field, fieldState }) => (
                <FormField
                  error={fieldState.error?.message}
                  label={index === 0 ? "Space" : undefined}
                  tooltipInfoVariant="modal"
                  tooltipMode="parent"
                  tooltipInfo={
                    <>
                      <TooltipModalTitle>Space access</TooltipModalTitle>
                      <TooltipModalBody align="start">
                        Assign access to dedicated spaces, you can add more than one access type and
                        create access rules.
                        <ReadMoreDocsLink docsUrl={getDocsUrl("/concepts/spaces")} />
                      </TooltipModalBody>
                    </>
                  }
                  fullWidth
                  noMargin
                  className={styles.formField}
                >
                  {({ ariaInputProps }) => (
                    <SpaceSelect
                      value={value.space}
                      disabledIds={disabledSpaces}
                      onChange={field.onChange}
                      error={!!fieldState.error?.message}
                      disabled={isDisabled || !areSpacesOptionsAvailable}
                      ariaProps={ariaInputProps}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name={`spaces.${index}.spaceAccessLevel`}
              control={control}
              rules={{
                validate: (val) => validateAccessLevelField(val, index),
              }}
              render={({ field, fieldState }) => (
                <FormField
                  error={fieldState.error?.message}
                  label={index === 0 ? "Role" : undefined}
                  tooltipInfoVariant="modal"
                  tooltipMode="parent"
                  tooltipInfo={
                    <>
                      <TooltipModalTitle>Choose access type</TooltipModalTitle>
                      <TooltipModalBody align="start">
                        <Typography tag="p" variant="p-body3">
                          <Typography tag="span" variant="p-t7">
                            Read
                          </Typography>{" "}
                          - cannot create or modify neither stacks nor any attachable entities, but
                          can view them
                          <br />
                          <Typography tag="span" variant="p-t7">
                            Write
                          </Typography>{" "}
                          - an extension to Read, as it can actually trigger runs in the stacks it
                          sees
                          <br />
                          <Typography tag="span" variant="p-t7">
                            Admin
                          </Typography>{" "}
                          - can create and modify stacks and attachable entities, as well as trigger
                          runs
                        </Typography>

                        <ReadMoreDocsLink
                          docsUrl={getDocsUrl("/concepts/spaces/access-control#roles-and-rbac")}
                        />
                      </TooltipModalBody>
                    </>
                  }
                  fullWidth
                  noMargin
                  className={styles.formField}
                >
                  {({ ariaInputProps }) => (
                    <FormFieldRowWrapper>
                      <Select
                        placeholder="Please select"
                        value={value.spaceAccessLevel ?? ""}
                        items={spaceAccessLevelOptions}
                        onChange={field.onChange}
                        isDisabled={isDisabled}
                        {...ariaInputProps}
                      >
                        {FormFieldsSpacesRoleOption}
                      </Select>
                      {suffix}
                    </FormFieldRowWrapper>
                  )}
                </FormField>
              )}
            />
          </Box>
        )}
      </FormArrayField>
    </Box>
  );
};

export default FormFieldSpaces;
